import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { toast } from 'material-react-toastify';
import { z } from 'zod';

import DynamicSVGColor from '@/components/shared/DynamicSVGColor/DynamicSVGColor';
import { FilterSelect } from '@/components/shared/FilterSelect/FilterSelect';
import { TextField } from '@/components/shared/TextField/TextField';
import { ContentStatus, UpsertExploreCollectionRequest } from '@addmile/backend-client';
import { zodResolver } from '@hookform/resolvers/zod';

import { COLOR_PRESETS, CONTENT_STATUS, SUPPORTED_LANGUAGES } from '../../const';
import { useContentQueries } from '../../services/ContentManagerService';
import CreateCollectionFormHeader from './CreateCollectionFormHeader';
import { createCollectionSchema } from './schema';

type FormData = z.infer<typeof createCollectionSchema>;

type CreateCollectionFormProps = {
  onCancel: () => void;
  onSaved: () => void;
  initial?: {
    slug: string;
    title: string;
    imageUrl: string;
    status: ContentStatus;
    personalization: string;
    language: string;
    color: string;
  };
};

const CreateCollectionForm = ({ onCancel, onSaved, initial }: CreateCollectionFormProps) => {
  const { useUpsertCollection } = useContentQueries();
  const upsert = useUpsertCollection();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(createCollectionSchema),
    defaultValues: {
      language: (initial?.language as 'en' | 'es') || 'en',
      status: initial?.status || ContentStatus.Offline,
      color: initial?.color || COLOR_PRESETS[0],
      slug: initial?.slug || '',
      title: initial?.title || '',
      personalization: initial?.personalization || '',
      imageUrl: initial?.imageUrl || '',
    },
  });

  React.useEffect(() => {
    if (initial) {
      reset({
        language: (initial.language as 'en' | 'es') || 'en',
        status: initial.status || ContentStatus.Offline,
        color: initial.color || COLOR_PRESETS[0],
        slug: initial.slug || '',
        title: initial.title || '',
        personalization: initial.personalization || '',
        imageUrl: initial.imageUrl || '',
      });
    }
  }, [initial, reset]);

  const selectedColor = watch('color');

  const onSubmit = async (form: FormData) => {
    try {
      const payload: UpsertExploreCollectionRequest = {
        title: form.title,
        imageUrl: form.imageUrl || '',
        status: form.status,
        personalization: form.personalization,
        language: form.language,
        color: form.color || COLOR_PRESETS[0],
      };
      await upsert.mutateAsync({ slug: form.slug, data: payload });
      toast.success('Collection saved');
      onSaved();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      const msg = err?.response?.data?.message || err?.message || 'Failed to save collection';
      toast.error(msg);
    }
  };

  const isEdit = Boolean(initial?.slug);

  const handleFormSubmit = () => {
    handleSubmit(onSubmit, (validationErrors) => {
      const errorFields = Object.keys(validationErrors);
      if (errorFields.length > 0) {
        const errorMessages = errorFields.map((field) => {
          const error = validationErrors[field as keyof typeof validationErrors];
          return error?.message || `${field} is invalid`;
        });
        toast.error(`Please fix the following errors: ${errorMessages.join(', ')}`);
      }
    })();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-[944px]">
      <CreateCollectionFormHeader
        isEdit={isEdit}
        isSubmitting={isSubmitting}
        onBack={onCancel}
        onSubmit={handleFormSubmit}
      />

      <div className="grid grid-cols-3 gap-6 w-full max-w-[944px]">
        <div>
          <label className="block text-sm font-semibold text-neutral-80 mb-2">Title</label>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Type here..."
                className="!w-full"
                inputClassName="!border !border-neutral-20 !font-medium"
              />
            )}
          />
          {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-neutral-80 mb-2">
            Collection slug
          </label>
          <Controller
            name="slug"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="example-slug"
                className="!w-full"
                disabled={isEdit}
                inputClassName="!border !border-neutral-20 !font-medium"
              />
            )}
          />
          {errors.slug && <p className="text-sm text-red-600 mt-1">{errors.slug.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-80 mb-2">
            Personalization Tag
          </label>
          <Controller
            name="personalization"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="get-ex-back"
                className="!w-full"
                inputClassName="!border !border-neutral-20 !font-medium"
              />
            )}
          />
          {errors.personalization && (
            <p className="text-sm text-red-600 mt-1">{errors.personalization.message}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-neutral-80 mb-2">
          Cover Image (URL)
        </label>
        <Controller
          name="imageUrl"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder="https://example.com/image.png"
              className="!w-full"
              inputClassName="!border !border-neutral-20 !font-medium"
            />
          )}
        />
        {errors.imageUrl && <p className="text-sm text-red-600 mt-1">{errors.imageUrl.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-neutral-80 mb-3">Card Color</label>
        <div className="flex items-center gap-4">
          {COLOR_PRESETS.map((color) => {
            const isSelected = selectedColor === color;
            return (
              <button
                key={color}
                type="button"
                onClick={() => setValue('color', color)}
                className="relative h-14 w-20 rounded-xl overflow-hidden"
                style={{
                  backgroundColor: color,
                  border: isSelected ? '2px solid #3B82F6' : '1px solid #D1D5DB',
                }}
              >
                {isSelected && (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                  >
                    <DynamicSVGColor icon="/icons/check.svg" className="w-6 h-6" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {errors.color && <p className="text-sm text-danger-60 mt-1">{errors.color.message}</p>}
      </div>

      <div className="flex gap-6 items-center">
        <Controller
          name={'language'}
          control={control}
          render={({ field }) => (
            <FilterSelect
              value={field.value}
              onChange={(v) => field.onChange(v)}
              options={SUPPORTED_LANGUAGES}
              labelClassName="font-semibold"
              label="Language"
            />
          )}
        />
        {errors.language && (
          <p className="text-sm text-red-600 mt-1">{errors.language?.message as string}</p>
        )}

        <Controller
          name={'status'}
          control={control}
          render={({ field }) => (
            <FilterSelect
              label="Status"
              labelClassName="font-semibold"
              value={field.value as ContentStatus}
              onChange={(v) => field.onChange(v)}
              options={CONTENT_STATUS.map(({ value, label, icon, color }) => ({
                value,
                label,
                icon,
                color,
              }))}
            />
          )}
        />
      </div>
    </form>
  );
};

export default CreateCollectionForm;
